import { AfterViewInit, ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';

// PrimeNG Components
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { ContextMenuModule } from 'primeng/contextmenu';
import { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { ContextMenu } from 'primeng/contextmenu';

// Shared Components
import { SidebarComponent } from './sidebar/sidebar.component';
import { HeaderComponent } from './header/header.component';
import { OverlayComponent } from './overlay/overlay.component';
import { FooterComponent } from './footer/footer.component';
import { TabStateService } from '@shared/services/tab-state.service';
// Services and Stores
import { settingStore } from '@core/store/setting.store';

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: {
    '(window:resize)': 'onResize($event)',
  },
  imports: [
    CommonModule,
    RouterOutlet,
    SidebarComponent,
    HeaderComponent,
    OverlayComponent,
    FooterComponent,
    TabsModule,
    TooltipModule,
    MenuModule,
    ContextMenuModule
  ],
})
export class MainLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly settingStore = inject(settingStore);
  private readonly tabService = inject(TabStateService);

  @ViewChild('tabsDropdownMenu') tabsDropdownMenu!: Menu;
  @ViewChild('tabContextMenu') tabContextMenu!: ContextMenu;
  @ViewChild('tabsHeaderHost') tabsHeaderHost!: ElementRef<HTMLElement>;

  darkMode = false;
  sidebarToggle = false;
  sidebarVisible = false;
  isMobile = false;
  overlayVisible = false;
  
  // Reactive tab state using computed signals from service
  tabs = computed(() => this.tabService.getTabs());
  activeIndex = computed(() => this.tabService.getActiveIndex());

  // Tab management state
  selectedTabIndex: number = 0;

  activeTabValue = signal<string | number | undefined>(undefined);
  
  // Context menu state
  contextMenuItems: MenuItem[] = [];
  rightClickedTabIndex: number = -1;
  
  // Dropdown menu state
  dropdownMenuItems = computed<MenuItem[]>(() => {
    const tabItems: MenuItem[] = this.tabs().map((tab, index) => ({
      label: tab.title,
      icon: tab.icon || 'pi pi-file',
      command: () => this.onTabChange(index),
      styleClass: index === this.activeIndex() ? 'active-tab-item' : ''
    }));

    // Extra actions
    tabItems.push(
      { separator: true },
      {
        label: 'Cerrar todas',
        icon: 'pi pi-trash',
        disabled: this.tabs().length === 0,
        command: () => this.onCloseAllTabsFromDropdown(),
      }
    );

    return tabItems;
  });
  
  // Dropdown visibility state
  showDropdown = signal(false);
  
  // ResizeObserver instance
  private resizeObserver?: ResizeObserver;

  constructor() {
    this.checkScreenSize();

    effect(() => {
      this.sidebarToggle = this.settingStore.data.miniMenu();
    });
    
    // Check overflow when tabs change
    effect(() => {
      this.tabs(); // Subscribe to tabs changes
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => this.checkTabsOverflow(), 0);
    });

    effect(() => {
      const currentTabs = this.tabs();
      const idx = this.activeIndex();
      const nextValue = idx >= 0 && idx < currentTabs.length ? currentTabs[idx]!.id : undefined;
      this.activeTabValue.set(nextValue);
      this.scheduleScrollActiveTabIntoView();
    });
  }

  ngOnInit() {
    // Initialize layout
  }

  ngAfterViewInit() {
    // Initial check for tabs overflow
    this.checkTabsOverflow();
    
    // Setup ResizeObserver to detect container size changes
    if (this.tabsHeaderHost) {
      this.resizeObserver = new ResizeObserver(() => {
        this.checkTabsOverflow();
      });
      this.resizeObserver.observe(this.tabsHeaderHost.nativeElement);
    }
  }

  ngOnDestroy() {
    // Cleanup ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  onResize(_event: UIEvent) {
    this.checkScreenSize();
    this.checkTabsOverflow();
  }

  private checkScreenSize() {
    this.isMobile = window.innerWidth < 768;
    if (!this.isMobile) {
      this.sidebarVisible = false;
      this.overlayVisible = false;
    }
  }

  onSidebarToggle() {
    if (this.isMobile) {
      this.sidebarVisible = !this.sidebarVisible;
      this.overlayVisible = this.sidebarVisible;
    } else {
      const miniMenu = this.settingStore.data.miniMenu();
      this.settingStore.setMiniMenu(!miniMenu);
    }
  }

  onOverlayClick() {
    this.sidebarVisible = false;
    this.overlayVisible = false;
  }

  onSidebarCollapsedChange(collapsed: boolean): void {
    if (!this.isMobile) {
      this.settingStore.setMiniMenu(collapsed);
    }
  }

  onTabChange(index: number): void {
    this.tabService.setActiveTab(index);
    this.scheduleScrollActiveTabIntoView();
  }

  onTabClose(event: Event, index: number): void {
    event.stopPropagation();
    this.tabService.closeTab(index);
  }

  onTabRightClick(event: MouseEvent, index: number): void {
    event.preventDefault();
    event.stopPropagation();
    this.rightClickedTabIndex = index;
    this.buildContextMenu(index);
    this.tabContextMenu.show(event);
  }

  onTabsAreaRightClick(event: MouseEvent): void {
    event.preventDefault();

    const currentTabs = this.tabs();
    if (currentTabs.length === 0) {
      return;
    }

    const baseIndex = this.getBaseIndexForTabsAreaContextMenu(currentTabs.length);
    this.buildTabsAreaContextMenu(baseIndex);
    this.tabContextMenu.show(event);
  }

  private buildContextMenu(index: number): void {
    const currentTabs = this.tabs();
    const tab = currentTabs[index];
    const isOnlyTab = currentTabs.length === 1;
    const hasTabsToLeft = index > 0;
    const hasTabsToRight = index < currentTabs.length - 1;

    this.contextMenuItems = [
      {
        label: 'Cerrar',
        icon: 'pi pi-times',
        disabled: !tab.closable,
        command: () => this.onTabClose(new Event('click'), index)
      },
      {
        separator: true
      },
      {
        label: 'Cerrar otros',
        icon: 'pi pi-times-circle',
        disabled: isOnlyTab,
        command: () => this.closeOtherTabs(index)
      },
      {
        label: 'Cerrar a la izquierda',
        icon: 'pi pi-angle-double-left',
        disabled: !hasTabsToLeft,
        command: () => this.tabService.closeTabsToLeft(index)
      },
      {
        label: 'Cerrar a la derecha',
        icon: 'pi pi-angle-double-right',
        disabled: !hasTabsToRight,
        command: () => this.tabService.closeTabsToRight(index)
      },
      {
        separator: true
      },
      {
        label: 'Cerrar todos',
        icon: 'pi pi-trash',
        disabled: currentTabs.length === 0,
        command: () => this.tabService.closeAllTabs()
      }
    ];
  }

  private buildTabsAreaContextMenu(index: number): void {
    const currentTabs = this.tabs();
    const hasTabsToLeft = index > 0;
    const hasTabsToRight = index < currentTabs.length - 1;

    this.contextMenuItems = [
      {
        label: 'Cerrar a la izquierda',
        icon: 'pi pi-angle-double-left',
        disabled: !hasTabsToLeft,
        command: () => this.tabService.closeTabsToLeft(index),
      },
      {
        label: 'Cerrar a la derecha',
        icon: 'pi pi-angle-double-right',
        disabled: !hasTabsToRight,
        command: () => this.tabService.closeTabsToRight(index),
      },
      { separator: true },
      {
        label: 'Cerrar todos',
        icon: 'pi pi-trash',
        disabled: currentTabs.length === 0,
        command: () => this.tabService.closeAllTabs(),
      },
    ];
  }

  private getBaseIndexForTabsAreaContextMenu(tabsLength: number): number {
    const lastRightClicked = this.rightClickedTabIndex;
    if (lastRightClicked >= 0 && lastRightClicked < tabsLength) {
      return lastRightClicked;
    }

    const active = this.activeIndex();
    if (active >= 0 && active < tabsLength) {
      return active;
    }

    return 0;
  }

  private closeOtherTabs(keepIndex: number): void {
    const currentTabs = this.tabs();
    const tabToKeep = currentTabs[keepIndex];
    
    if (tabToKeep) {
      // Close all tabs except the one at keepIndex
      this.tabService.closeOtherTabs();
    }
  }

  onTabsDropdownClick(event: Event): void {
    this.tabsDropdownMenu.toggle(event);
  }

  private onCloseAllTabsFromDropdown(): void {
    this.tabsDropdownMenu?.hide();
    this.tabService.closeAllTabs();
  }

  // Check if tabs overflow and need dropdown
  private checkTabsOverflow(): void {
    if (!this.tabsHeaderHost) {
      return;
    }

    const host = this.tabsHeaderHost.nativeElement;
    const tabList = host.querySelector('.p-tablist-tab-list') as HTMLElement | null;
    if (!tabList) {
      this.showDropdown.set(false);
      return;
    }

    const hasOverflow = tabList.scrollWidth > tabList.clientWidth;
    this.showDropdown.set(hasOverflow);
  }

  private scheduleScrollActiveTabIntoView(): void {
    requestAnimationFrame(() => this.scrollActiveTabIntoView());
  }

  private scrollActiveTabIntoView(): void {
    if (!this.tabsHeaderHost) {
      return;
    }

    const host = this.tabsHeaderHost.nativeElement;
    const tabList = host.querySelector('.p-tablist-tab-list') as HTMLElement | null;
    const active = tabList?.querySelector('[aria-selected="true"]') as HTMLElement | null;

    active?.scrollIntoView({
      behavior: 'smooth',
      inline: 'nearest',
      block: 'nearest',
    });
  }
}
